import {Divider, Typography} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Home from '@material-ui/icons/Home';
import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import AgentDocumentationRoutes from 'Agent/AgentDocumentation/AgentDocumentationRouteConstants';
import {AgentInformationRouteConstants} from 'Agent/AgentInformation/AgentInformationRouteConstants';
import CommissionRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import LegacyAccess from 'Core/components/Navigation/LegacyAccess';
import 'Core/components/Navigation/Sidebar.sass';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import * as O from 'fp-ts/lib/Option';
import * as React from 'react';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import NestedList from 'Shared/components/NestedList';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {UserRouteConstants} from 'Users/Routes';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import * as AgentNewsAdministrationRouteConstants from 'AgentNewsAdministration/AgentNewsAdministrationRouteConstants';
import {BOSToolsRouteConstants} from 'BOSTools/Routes';
import AgentDocumentationRouteConstants from 'Agent/AgentDocumentation/AgentDocumentationRouteConstants';
import {SystemAdministrationRouteConstants} from 'SystemAdministration/Routes';
import {SubscriptionsRouteConstants} from 'GenericTexting/Routes';

type SidebarProps = {
  isOpen: boolean;
  onSidebarClose: () => void;
} & ReturnType<typeof mapStateToProps> &
  RouteComponentProps;

export const Sidebar = (props: SidebarProps) => {
  const withSidebarClose = (func: () => void) => () => {
    func();
    props.onSidebarClose();
  };

  const isAtCurrentRoute = (value: string) => props.location.pathname === value;
  const isAtEnrollTrackRoute = (routeProps: RouteComponentProps) =>
    [
      EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD,
      EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS,
      EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING,
      EnrollmentRouteConstants.UPLOAD_ENROLLMENT_FILE_APPLICATIONS,
      EnrollmentRouteConstants.EMPLOYER_ENROLLMENTS,
    ].includes(routeProps.location.pathname);

  const isAtMessagingRoute = (routeProps: RouteComponentProps) =>
    [
      SubscriptionsRouteConstants.MESSAGES,
      SubscriptionsRouteConstants.CLIENT_MESSAGES,
      SubscriptionsRouteConstants.CLIENTS,
      SubscriptionsRouteConstants.MESSAGE_DISPOSITIONS,
    ].includes(routeProps.location.pathname);

  const isAtMyAccessRoute = [
    AccountRouteConstants.ACCOUNTS,
    CommissionRouteConstants.COMMISSIONS,
    AgentInformationRouteConstants.NY_RATE_GENERATORS,
    PolicyRouteConstants.POLICIES,
    PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(),
    Ten99RouteConstants.TEN99s,
  ].some(isAtCurrentRoute);

  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);

  return (
    <nav>
      <Drawer open={props.isOpen} onClose={props.onSidebarClose}>
        <div className="sidebar">
          <Typography className="sidebar__header" variant="h6" color="primary">
            Main Menu
          </Typography>
          <Divider />
          <List>
            <ListItem
              selected={isAtCurrentRoute(CoreRouteConstants.DASHBOARD)}
              button
              onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.DASHBOARD))}
            >
              <ListItemIcon>
                <Home className="sidebar__dashboard-icon" />
              </ListItemIcon>
              <ListItemText primary={'Home'} />
            </ListItem>
            <RequireScope scope={Scope.SearchEnrollmentFiles} scopes={props.scopes}>
              <NestedList isExpanded={isAtEnrollTrackRoute(props)} mainListItemTitle="Enroll Track">
                <>
                  <ListItem
                    selected={isAtCurrentRoute(EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD)}
                    button
                    onClick={withSidebarClose(() => props.history.push(EnrollmentRouteConstants.ENROLLMENTS_DASHBOARD))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-chart-bar sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Dashboard'} />
                  </ListItem>
                  {/* <ListItem
                    selected={isAtCurrentRoute(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING)}
                    button
                    onClick={withSidebarClose(() =>
                      props.history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING)
                    )}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Enrollment Files'} />
                  </ListItem> */}
                  <ListItem
                    selected={isAtCurrentRoute(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS)}
                    button
                    onClick={withSidebarClose(() =>
                      props.history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS)
                    )}
                  >
                    <ListItemIcon>
                      <i className="fa fa-frown sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Enrollment File Errors'} />
                  </ListItem>
                  <RequireAtLeastOneScope scopes={[Scope.EnrollTrackAdministrator, Scope.UploadBrokerSpreadsheet]}>
                    <ListItem
                      selected={isAtCurrentRoute(EnrollmentRouteConstants.UPLOAD_ENROLLMENT_FILE_APPLICATIONS)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(EnrollmentRouteConstants.UPLOAD_ENROLLMENT_FILE_APPLICATIONS)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'File Upload'} />
                    </ListItem>
                  </RequireAtLeastOneScope>
                  <ListItem
                    selected={isAtCurrentRoute(EnrollmentRouteConstants.EMPLOYER_ENROLLMENTS)}
                    button
                    onClick={withSidebarClose(() => props.history.push(EnrollmentRouteConstants.EMPLOYER_ENROLLMENTS))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Account Enrollments'} />
                  </ListItem>
                  <ListItem
                    selected={isAtCurrentRoute(EnrollmentRouteConstants.EXTERNAL_UPLOAD_QUEUE)}
                    button
                    onClick={withSidebarClose(() => props.history.push(EnrollmentRouteConstants.EXTERNAL_UPLOAD_QUEUE))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Broker Spreadsheet Uploads'} />
                  </ListItem>
                </>
              </NestedList>
            </RequireScope>
            <RequireScope scope={Scope.ViewMessages} scopes={props.scopes}>
              <NestedList isExpanded={isAtMessagingRoute(props)} mainListItemTitle="Messaging">
                <>
                  <ListItem
                    selected={isAtCurrentRoute(SubscriptionsRouteConstants.MESSAGES)}
                    button
                    onClick={withSidebarClose(() => props.history.push(SubscriptionsRouteConstants.MESSAGES))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Messages'} />
                  </ListItem>
                  <ListItem
                    selected={isAtCurrentRoute(SubscriptionsRouteConstants.CLIENT_MESSAGES)}
                    button
                    onClick={withSidebarClose(() => props.history.push(SubscriptionsRouteConstants.CLIENT_MESSAGES))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Client Messages'} />
                  </ListItem>
                  <ListItem
                    selected={isAtCurrentRoute(SubscriptionsRouteConstants.CLIENTS)}
                    button
                    onClick={withSidebarClose(() => props.history.push(SubscriptionsRouteConstants.CLIENTS))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Clients'} />
                  </ListItem>
                  <ListItem
                    selected={isAtCurrentRoute(SubscriptionsRouteConstants.MESSAGE_DISPOSITIONS)}
                    button
                    onClick={withSidebarClose(() =>
                      props.history.push(SubscriptionsRouteConstants.MESSAGE_DISPOSITIONS)
                    )}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Dispositions'} />
                  </ListItem>
                  <ListItem
                    selected={isAtCurrentRoute(SubscriptionsRouteConstants.KEYWORDS)}
                    button
                    onClick={withSidebarClose(() => props.history.push(SubscriptionsRouteConstants.KEYWORDS))}
                  >
                    <ListItemIcon>
                      <i className="fa fa-file-alt sidebar__menu-icon" />
                    </ListItemIcon>
                    <ListItemText primary={'Keywords'} />
                  </ListItem>
                </>
              </NestedList>
            </RequireScope>
            <RequireAtLeastOneScope
              scopes={[
                Scope.SearchAccounts,
                Scope.PolicySearch,
                Scope.SearchTaxStatements,
                Scope.SearchCommissions,
                Scope.ViewPendingUnderwritingRequirements,
              ]}
            >
              <NestedList isExpanded={isAtMyAccessRoute} mainListItemTitle="My Access">
                <>
                  <RequireScope scope={Scope.PolicySearch} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(PolicyRouteConstants.POLICIES)}
                      button
                      onClick={withSidebarClose(() => props.history.push(PolicyRouteConstants.POLICIES))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Policies'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.ViewCorrespondenceDetails} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES())}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES())
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-envelope-open-text sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Correspondence'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SearchCommissions} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(CommissionRouteConstants.COMMISSIONS)}
                      button
                      onClick={withSidebarClose(() => props.history.push(CommissionRouteConstants.COMMISSIONS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-money-check-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Commissions'} />
                    </ListItem>
                  </RequireScope>

                  {O.isSome(selectedAgency) && selectedAgency.value.isNewYork && (
                    <ListItem
                      selected={isAtCurrentRoute(AgentInformationRouteConstants.NY_RATE_GENERATORS)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentInformationRouteConstants.NY_RATE_GENERATORS)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-money-check-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Rate Generators'} />
                    </ListItem>
                  )}

                  <RequireScope scope={Scope.SearchTaxStatements} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(Ten99RouteConstants.TEN99s)}
                      button
                      onClick={withSidebarClose(() => props.history.push(Ten99RouteConstants.TEN99s))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file-invoice-dollar sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'1099s'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SendServiceRequest} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(PolicyRouteConstants.POLICY_SERVICE_REQUEST)}
                      button
                      onClick={withSidebarClose(() => props.history.push(PolicyRouteConstants.POLICY_SERVICE_REQUEST))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-handshake sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Policy Service Request'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SearchAccounts} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AccountRouteConstants.ACCOUNTS)}
                      button
                      onClick={withSidebarClose(() => props.history.push(AccountRouteConstants.ACCOUNTS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-briefcase sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Accounts'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.ViewAgentHierarchy} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AgentInformationRouteConstants.HIERARCHY)}
                      button
                      onClick={withSidebarClose(() => props.history.push(AgentInformationRouteConstants.HIERARCHY))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-users sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Agent Hierarchy'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SearchCommissionSchedules} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(CommissionRouteConstants.COMMISSION_SCHEDULES)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(CommissionRouteConstants.COMMISSION_SCHEDULES)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-calendar-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Commission Schedules'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SearchAccounts} scopes={props.scopes}>
                    <ListItem
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AccountRouteConstants.POLICY_INFORMATION_REPORTS)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-info sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Policy Information Reports'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.ViewPendingUnderwritingRequirements} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(PolicyRouteConstants.PENDING_UNDERWRITING_REQUIREMENT)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(PolicyRouteConstants.PENDING_UNDERWRITING_REQUIREMENT)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Pending Underwriting Requirements'} />
                    </ListItem>
                  </RequireScope>
                  {O.isSome(selectedAgency) && !selectedAgency.value.isNewYork && (
                    <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                      <ListItem
                        button
                        onClick={withSidebarClose(() => window.open('http://allstatevoluntary.com/qrt/qrtrd.php'))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-lightbulb sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Training Site'} />
                      </ListItem>
                    </RequireScope>
                  )}
                  <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                    <ListItem
                      button
                      onClick={withSidebarClose(() =>
                        downloadFile(
                          '/api/v1/agents/documents/contactUs' +
                            addFilterParameters({
                              encryptedIdentifier: O.toUndefined(selectedAgency)?.encryptedIdentifier,
                            }),
                          'AB Contact Brochure.pdf'
                        )
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-address-card sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Contact Us'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.AgentLicense} scopes={props.scopes}>
                    <ListItem
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentInformationRouteConstants.AGENT_LICENSES)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-id-badge sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Agent Licenses'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.SearchEnrollmentApplications} scopes={props.scopes}>
                    <ListItem
                      button
                      onClick={withSidebarClose(() => props.history.push(PolicyRouteConstants.POLICY_APPLICATIONS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file-signature sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Policy Applications'} />
                    </ListItem>
                  </RequireScope>
                </>
              </NestedList>
            </RequireAtLeastOneScope>
            <RequireAtLeastOneScope
              scopes={[
                Scope.SearchExclusiveAgents,
                Scope.SearchAgentUsers,
                Scope.SearchPortalUsers,
                Scope.AlertsSearch,
              ]}
            >
              <NestedList
                mainListItemTitle="Administration"
                isExpanded={isAtCurrentRoute(AgencyAdministrationRouteConstants.AGENTS)}
              >
                <>
                  {/* <RequireScope scope={Scope.AgentNewsAdministration} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AgentNewsAdministrationRouteConstants.NewsAdministration)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentNewsAdministrationRouteConstants.NewsAdministration)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-news sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText>Agent News</ListItemText>
                    </ListItem>
                  </RequireScope> */}
                  {/* Retirement of API's */}
                  {/* <RequireScope scope={Scope.SearchAgentUsers} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AgencyAdministrationRouteConstants.AGENTS)}
                      button
                      onClick={withSidebarClose(() => props.history.push(AgencyAdministrationRouteConstants.AGENTS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-user sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText>Agents</ListItemText>
                    </ListItem>
                  </RequireScope> */}
                  <RequireScope scope={Scope.SearchPortalUsers} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(UserRouteConstants.USERS)}
                      button
                      onClick={withSidebarClose(() => props.history.push(UserRouteConstants.USERS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-users sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText>Users</ListItemText>
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.AlertsSearch} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(CoreRouteConstants.ALERTS)}
                      button
                      onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.ALERTS))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-bell sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText>Alerts</ListItemText>
                    </ListItem>
                  </RequireScope>
                  {/* <RequireScope scope={Scope.ReadETLDashboard} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(SystemAdministrationRouteConstants.ETL_DASHBOARD)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(SystemAdministrationRouteConstants.ETL_DASHBOARD)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-chart sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText>ETL Dashboard</ListItemText>
                    </ListItem>
                  </RequireScope> */}
                </>
              </NestedList>
            </RequireAtLeastOneScope>
            <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
              <NestedList isExpanded={isAtMyAccessRoute} mainListItemTitle="Documentation">
                <>
                  <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                    <ListItem
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentDocumentationRoutes.ALL_APP_AND_ENROLL_MY_WAY)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-briefcase sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'AllApp and EnrollMyWay Voice Upload'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AgentDocumentationRoutes.DIRECT_DEPOSIT_INFORMATION)}
                      button
                      id="view-direct-deposit-option"
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentDocumentationRoutes.DIRECT_DEPOSIT_INFORMATION)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-dollar-sign sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Direct Deposit'} />
                    </ListItem>
                  </RequireScope>
                  <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                    <ListItem
                      selected={isAtCurrentRoute(AgentDocumentationRouteConstants.PRODUCER_COMPLIANCE_POLICY)}
                      button
                      onClick={withSidebarClose(() =>
                        props.history.push(AgentDocumentationRouteConstants.PRODUCER_COMPLIANCE_POLICY)
                      )}
                    >
                      <ListItemIcon>
                        <i className="fa fa-balance-scale sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Producer Compliance Policy'} />
                    </ListItem>
                  </RequireScope>
                </>
              </NestedList>
            </RequireScope>
            <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
              <LegacyAccess onSidebarClose={props.onSidebarClose} />
            </RequireScope>
            <ListItem
              selected={isAtCurrentRoute(CoreRouteConstants.LEGAL_DOCUMENTS)}
              button
              id="view-direct-deposit-option"
              onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.LEGAL_DOCUMENTS))}
            >
              <ListItemIcon>
                <i className="fa fa-balance-scale sidebar__menu-icon" />
              </ListItemIcon>
              <ListItemText primary={'Legal Agreements'} />
            </ListItem>
            <RequireScope scope={Scope.BeneficiariesSearch} scopes={props.scopes}>
              <ListItem
                selected={isAtCurrentRoute(BeneficiariesRouteConstants.BENEFICIARIES)}
                button
                id="view-direct-deposit-option"
                onClick={withSidebarClose(() => props.history.push(BeneficiariesRouteConstants.BENEFICIARIES))}
              >
                <ListItemIcon>
                  <i className="fa fa-hand-holding-usd sidebar__menu-icon" />
                </ListItemIcon>
                <ListItemText primary={'Beneficiaries'} />
              </ListItem>
            </RequireScope>
            <RequireScope scope={Scope.BOSTools} scopes={props.scopes}>
              <ListItem
                selected={isAtCurrentRoute(BOSToolsRouteConstants.BOSTOOLS)}
                button
                id="view-direct-deposit-option"
                onClick={withSidebarClose(() => props.history.push(BOSToolsRouteConstants.BOSTOOLS))}
              >
                <ListItemIcon>
                  <i className="fa fa-cog sidebar__menu-icon" />
                </ListItemIcon>
                <ListItemText primary={'BOS Tools'} />
              </ListItem>
            </RequireScope>
          </List>
        </div>
      </Drawer>
    </nav>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
});

export default connect(mapStateToProps)(withRouter(Sidebar));
